/**
 * Role/permission helpers shared by route guards (enforcement) and nav filtering
 * (cosmetic). Both MUST use the same access map so what the UI shows and what the
 * guards allow can never drift. Role strings match the backend enum exactly
 * (see admin.graphqls / Role.java); the backend uppercases roles, so we do too.
 */

export type Role =
  | 'ADMIN'
  | 'FINANCE'
  | 'SALES_REP'
  | 'SALES_MANAGER'
  | 'OPS_MANAGER'
  | 'WAREHOUSE_USER'
  | 'PROCUREMENT_MANAGER'
  | 'PROCUREMENT_USER'
  | 'AUDITOR'

/** Platform module keys — mirror the keys in components/layout/ops/nav.ts. */
export type ModuleKey = 'inventory' | 'procurement' | 'accounting' | 'sales' | 'orders' | 'admin'

export const ALL_MODULES: ModuleKey[] = ['inventory', 'procurement', 'accounting', 'sales', 'orders', 'admin']

/**
 * Which modules each role may reach. ADMIN sees everything; domain roles see
 * their module; AUDITOR is read-only across the built operational modules.
 */
export const ROLE_MODULE_ACCESS: Record<Role, ModuleKey[]> = {
  ADMIN: ALL_MODULES,
  OPS_MANAGER: ['inventory', 'orders'],
  WAREHOUSE_USER: ['inventory', 'orders'],
  PROCUREMENT_MANAGER: ['procurement'],
  PROCUREMENT_USER: ['procurement'],
  FINANCE: ['accounting'],
  SALES_REP: ['sales', 'orders'],
  SALES_MANAGER: ['sales', 'orders'],
  AUDITOR: ['inventory', 'procurement', 'sales', 'orders', 'accounting', 'admin'],
}

/** Human-friendly labels for the raw role enum (used in the topbar/profile). */
export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Administrator',
  FINANCE: 'Finance',
  SALES_REP: 'Sales Rep',
  SALES_MANAGER: 'Sales Manager',
  OPS_MANAGER: 'Operations Manager',
  WAREHOUSE_USER: 'Warehouse User',
  PROCUREMENT_MANAGER: 'Procurement Manager',
  PROCUREMENT_USER: 'Procurement User',
  AUDITOR: 'Auditor',
}

/** Friendly label for the user's primary (first) role, or a neutral fallback. */
export function primaryRoleLabel(roles: readonly string[]): string {
  const norm = normalizeRoles(roles)
  return norm.length > 0 ? ROLE_LABELS[norm[0]] : 'Member'
}

export function normalizeRoles(roles: readonly string[] | undefined | null): Role[] {
  if (!roles) return []
  const valid = new Set<string>(Object.keys(ROLE_MODULE_ACCESS))
  return roles
    .map((r) => String(r).toUpperCase())
    .filter((r): r is Role => valid.has(r))
}

export function hasRole(roles: readonly string[], role: Role): boolean {
  return normalizeRoles(roles).includes(role)
}

export function hasAnyRole(roles: readonly string[], wanted: readonly Role[]): boolean {
  const mine = new Set(normalizeRoles(roles))
  return wanted.some((r) => mine.has(r))
}

/** Set of modules the given roles can access. */
export function modulesForRoles(roles: readonly string[]): Set<ModuleKey> {
  const result = new Set<ModuleKey>()
  for (const role of normalizeRoles(roles)) {
    for (const m of ROLE_MODULE_ACCESS[role]) result.add(m)
  }
  return result
}

export function canAccessModule(roles: readonly string[], moduleKey: ModuleKey): boolean {
  return modulesForRoles(roles).has(moduleKey)
}

/**
 * Which roles may perform standard WRITE actions (create/update/delete) in each
 * module. Mirrors the backend @RequireRole sets on the write operations exactly
 * (see the resolvers / application services). Read access is broader (see
 * ROLE_MODULE_ACCESS); AUDITOR is intentionally absent here — auditors are
 * read-only across every module. A few manager-only actions (approve quote/PO,
 * assign, deactivate) narrow further at the call site via hasAnyRole(...).
 */
export const ROLE_MODULE_WRITE: Record<ModuleKey, Role[]> = {
  inventory: ['OPS_MANAGER', 'ADMIN'],
  procurement: ['PROCUREMENT_MANAGER', 'PROCUREMENT_USER', 'ADMIN'],
  accounting: ['FINANCE', 'ADMIN'],
  sales: ['SALES_REP', 'SALES_MANAGER', 'ADMIN'],
  // Fulfilment "write" = raising/confirming/cancelling B2B orders (Sales-owned).
  orders: ['SALES_REP', 'SALES_MANAGER', 'ADMIN'],
  admin: ['ADMIN'],
}

/** True when the user's roles allow management writes (setup/config) in the module. */
export function canWrite(roles: readonly string[], moduleKey: ModuleKey): boolean {
  return hasAnyRole(roles, ROLE_MODULE_WRITE[moduleKey])
}

/**
 * Operational write roles — day-to-day transactions as opposed to setup/config.
 * In inventory a WAREHOUSE_USER may receive/transfer/adjust stock and run cycle
 * counts (operational) but may NOT manage products/warehouses/reorder rules
 * (that is `canWrite`/management). For every other module operational == write.
 */
export const ROLE_MODULE_OPERATE: Record<ModuleKey, Role[]> = {
  ...ROLE_MODULE_WRITE,
  inventory: ['WAREHOUSE_USER', 'OPS_MANAGER', 'ADMIN'],
  // Fulfilment "operate" = warehouse floor work (pick/pack/QC/dispatch), distinct
  // from the Sales-owned write actions above.
  orders: ['WAREHOUSE_USER', 'OPS_MANAGER', 'ADMIN'],
}

/** True when the user may perform day-to-day operational actions in the module. */
export function canOperate(roles: readonly string[], moduleKey: ModuleKey): boolean {
  return hasAnyRole(roles, ROLE_MODULE_OPERATE[moduleKey])
}
